import { Button, EmptyState, Icon, Spinner } from "@iec-id/ui";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isUnauthorized, redirectToOnboarding, redirectToSignIn } from "@/lib/session";
import { api } from "@/lib/trpc";

/**
 * Гейт витрины: шапка, меню и разделы рисуются только после живой сессии.
 *
 * Витрина целиком за cookie. Раньше оболочка монтировалась сразу, а 401 из
 * `auth.me` уводил на вход уже ПОСЛЕ первой отрисовки — в анонимной вкладке
 * на долю секунды был кабинет (меню, скелет Главной), потом форма входа.
 * Кэш по-прежнему ловит 401 (§6 frontend-state); гейт только не даёт
 * нарисовать витрину раньше ответа.
 */
export function SessionGate({ children }: { children: ReactNode }) {
	const { t } = useTranslation("common");
	const me = api.auth.me.useQuery(undefined, { retry: false });

	const unauthorized = isUnauthorized(me.error);
	const toOnboarding = me.data?.needsOnboarding === true;

	useEffect(() => {
		if (unauthorized) redirectToSignIn();
	}, [unauthorized]);

	useEffect(() => {
		if (toOnboarding) redirectToOnboarding();
	}, [toOnboarding]);

	if (me.isPending || unauthorized || toOnboarding) {
		return (
			<Splash>
				<PatientSpinner label={t("state.loading")} />
			</Splash>
		);
	}

	if (me.error || !me.data) {
		return (
			<Splash>
				<EmptyState
					icon={<Icon name="CircleAlert" size={32} />}
					title={t("state.errorTitle")}
					description={t("state.errorDescription")}
					action={
						<Button variant="second" onGrey onClick={() => void me.refetch()}>
							{t("state.retry")}
						</Button>
					}
				/>
			</Splash>
		);
	}

	return children;
}

function Splash({ children }: { children: ReactNode }) {
	return <div className="flex min-h-screen items-center justify-center bg-page">{children}</div>;
}

/** Сколько ждать, прежде чем признаться, что мы чего-то ждём. */
const SPINNER_DELAY_MS = 400;

/**
 * Спиннер, который не показывается на быстром ответе.
 *
 * `auth.me` отвечает за сотню миллисекунд, а анонимная вкладка вдобавок сразу
 * уходит на форму входа — и на этом пути кружок успевал только моргнуть.
 * Мелькнувший индикатор читается как подёргивание интерфейса, а не как
 * «идёт загрузка»: спокойная канва в те же 200мс выглядит стабильнее.
 *
 * Подпись живёт в `aria-live` с первого кадра: скринридеру ждать нечего, ему
 * состояние надо объявить сразу.
 */
function PatientSpinner({ label }: { label: string }) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setVisible(true), SPINNER_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);

	return visible ? (
		<Spinner size={28} label={label} />
	) : (
		<span role="status" aria-live="polite" className="sr-only">
			{label}
		</span>
	);
}
